import AlternateColors from '@components/Product/Details/_NewAlternateColors';
import { useProductVariations } from '@hooks/useProductVariations';
import Product from '@models/product';
import { StateSetterFunction } from '@repo/ui/src/types/helpers';
import { ProductVariation, VariationGroup } from '@typings/product';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React from 'react';

export interface TierThreeVariationsProps {
  product: Product;
  activeVariation: ProductVariation | undefined;
  setActiveVariation: StateSetterFunction<ProductVariation | undefined>;
}

const TierThreeVariations = ({
  product,
  activeVariation,
  setActiveVariation,
}: TierThreeVariationsProps) => {
  const { t } = useTranslation();

  const [color, size] = useProductVariations(product, [
    VariationGroup.apparelColor,
    VariationGroup.apparelSize,
  ]);

  return (
    <>
      <ul className="tw-hidden md:tw-flex tw-list-none tw-px-0 tw-gap-2 tw-flex-wrap">
        {size.variations?.map((variation) => {
          const value = variation.attributesMap[VariationGroup.apparelSize];
          return (
            <li key={value}>
              <button
                onClick={() => setActiveVariation(variation!)}
                className={classNames(
                  'tw-w-12 tw-aspect-square tw-bg-white tw-grid tw-place-content-center tra-text-web-utl-button-m tw-transition-all tw-relative',
                  variation!.id === activeVariation?.id
                    ? 'tw-border-4 tw-border-action-500 tw-text-action-400'
                    : 'tw-border-2 tw-border-surface-450 disabled:tw-border-0 disabled:tw-text-surface-400',
                )}
                disabled={!variation!.isOrderable}
                aria-label={`${value} ${!variation!.isOrderable && 'Out of Stock'}`}
              >
                {value}
                {variation!.id !== activeVariation?.id && !variation!.isOrderable && (
                  <span className="tw-absolute tw-inset-0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 49 49" fill="none">
                      <path stroke="currentColor" strokeWidth="2" d="M1.5 2h46v46h-46z" />
                      <path
                        stroke="currentColor"
                        strokeLinecap="square"
                        strokeWidth="2"
                        d="M1.5 47.448 46.898 2"
                      />
                    </svg>
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="md:tw-hidden tw-grid tw-gap-y-2">
        <h4 className="tra-text-web-utl-link">
          {t('productDetailPageContent.tierThreeSelectLabel', undefined, {
            default: 'Size',
          })}
        </h4>
        <div className="tw-relative tw-grid after:tw-block after:tw-absolute after:tw-bg-dropdown after:tw-bg-cover after:tw-inset-y-4 after:tw-right-4 after:tw-w-6 after:tw-pointer-events-none">
          <select
            onChange={(e) =>
              setActiveVariation(
                size.variations?.find(
                  (variation) =>
                    variation.attributesMap[VariationGroup.apparelSize] === e.target.value,
                ),
              )
            }
            className="tw-bg-white tw-border-2 tw-border-surface-450 tw-px-4 tw-py-3 tw-w-full"
          >
            <option value="default" key="default" selected disabled hidden>
              -
            </option>
            {size.variations?.map((variation) => (
              <option
                key={variation!.id}
                value={variation.attributesMap[VariationGroup.apparelSize]}
                selected={variation!.id === activeVariation?.id}
                disabled={!variation!.isOrderable}
              >
                {variation.attributesMap[VariationGroup.apparelSize]}
                {!variation!.isOrderable && ' - Out of Stock'}
              </option>
            ))}
          </select>
        </div>
      </div>
      {color.variations && color.variations.length > 1 && color.active && (
        <AlternateColors
          variationGroup={VariationGroup.apparelColor}
          activeColor={color.active}
          colors={color.variations}
        />
      )}
    </>
  );
};

export default TierThreeVariations;
